import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { ColorResult } from 'react-color';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import { saveMediaItem } from '../../stores/actions/media';
import { setModalState } from '../../stores/actions/modal';
import { MediaItem } from '../../stores/reducers/media';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { GreenroomUser } from '../../stores/reducers/session';
import { GreenroomState } from '../../stores/types/types';
import { isVideo } from '../../utils/utils';
import { ICONS } from '../general-components/icon';
import RightClickMenu, { MenuItem } from '../general-components/right-click-menu';
import { VIDEO_EDITOR_PAGES } from '../modal/templates/video-editor/video-editor';
import ColorPicker from '../right-panel/settings/color-picker';
import useMedia from '../../hooks/use-media';
import Colors from '../../utils/colors';

interface Props {
	clickCoordinates?: [number, number];
	contextMenuId: string;
	mediaItem: MediaItem;
	socket: Socket | null;
	user: GreenroomUser;
	canEditMedia: boolean;
}

const baseColorProps: ColorResult = {
	hsl: {
		h: 0,
		s: 0,
		l: 8,
		a: 1,
	},
	rgb: {
		r: 21,
		g: 21,
		b: 21,
		a: 1,
	},
	hex: Colors.initialBackgroundColor,
};

export default function FeedMediaContextMenu(props: Props) {
	const dispatch = useDispatch();
	const {
		clickCoordinates, contextMenuId, mediaItem, socket, user,
	} = props;

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);

	const mediaSettingsDebounce = useRef<NodeJS.Timeout | null>(null);
	const timmy = useRef<NodeJS.Timeout | null>(null); // a little lamb with a lot to learn

	const [lastColorProps, setLastColorProps] = useState<ColorResult | undefined>();
	const [colorProps, setColorProps] = useState<ColorResult | undefined>({
		...baseColorProps,
		hex: mediaItem.media_data.background_color || baseColorProps.hex,
	});

	const {
		canTrimMediaItem,
	} = useMedia();

	const canTrim = useMemo((): boolean => (
		canTrimMediaItem(mediaItem)
	), [canTrimMediaItem, mediaItem]);

	const handleMediaSettings = (type: string, value: boolean | string) => {
		if (!mediaItem || !token || !user) return;
		if (mediaSettingsDebounce.current) clearTimeout(mediaSettingsDebounce.current);

		mediaSettingsDebounce.current = setTimeout(() => {
			const newMediaData = JSON.parse(JSON.stringify(mediaItem.media_data));
			if (newMediaData[type] !== value) {
				newMediaData[type] = value;
				dispatch(
					saveMediaItem(
						token,
						{ ...mediaItem, media_data: newMediaData, edited_by: user.gr_user },
						undefined,
					),
				);
				socket?.setMedia({ ...mediaItem, media_data: newMediaData, edited_by: user.gr_user });
			}
		}, 100);
	};

	// Debounces background color updates
	useEffect(() => {
		if (timmy.current) clearTimeout(timmy.current);
		timmy.current = setTimeout(() => {
			if (colorProps?.hex) {
				handleMediaSettings('background_color', colorProps.hex);
			}
		}, 300);
	}, [colorProps]);

	const toggleBackgroundActive = () => {
		if (mediaItem.media_data.background_color && mediaItem.media_data.background_color !== 'none') {
			setLastColorProps(colorProps);
			setColorProps(undefined);
			handleMediaSettings('background_color', 'none');
		} else {
			setColorProps(lastColorProps ?? baseColorProps);
			handleMediaSettings('background_color', lastColorProps?.hex ?? baseColorProps.hex);
		}
	};

	const handleTrim = () => {
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.VIDEO_EDITOR,
			modalData: {
				mediaId: mediaItem.id,
				editorPage: mediaItem.num_pages > 1
					? VIDEO_EDITOR_PAGES.SELECT_MEDIA_PAGE
					: VIDEO_EDITOR_PAGES.TRIM_EDITOR,
			},
		}));
	};

	const menuItems: MenuItem[] = useMemo(() => [
		{
			hidden: !mediaItem.media_data.fill,
			handleClick: () => handleMediaSettings('fill', false),
			iconName: ICONS.FIT,
			text: 'Fit',
		},
		{
			hidden: mediaItem.media_data.fill,
			handleClick: () => handleMediaSettings('fill', true),
			iconName: ICONS.FULLSCREEN,
			text: 'Fill',
		},
		{
			hidden: !isVideo(mediaItem.images),
			handleClick: () => handleMediaSettings('loop', !mediaItem.media_data.loop),
			checkboxValue: !!mediaItem.media_data.loop,
			text: 'Loop',
		},
		{
			handleClick: toggleBackgroundActive,
			checkboxValue: mediaItem.media_data.background_color !== 'none',
			text: 'Background',
		},
		{
			iconName: ICONS.DESIGN,
			text: 'Background',
			disabled: mediaItem.media_data.background_color === 'none',
			CustomSubMenu: (
				<div style={{ width: '350px', marginTop: '-50px' }}>
					<ColorPicker
						value={colorProps?.hex || mediaItem.media_data.background_color}
						color={colorProps}
						label={'Background Color'}
						open
						onChange={(color: ColorResult) => setColorProps(color)}
						styleProp={{ left: -10 }}
					/>
				</div>
			),
			dontClose: true,
		},
		{
			hidden: !canTrim,
			handleClick: handleTrim,
			iconName: ICONS.MEDIA_TRIM,
			text: 'Trim',
		},

	], [colorProps, mediaItem]);

	return (
	<RightClickMenu
		contextMenuId={contextMenuId}
		items={menuItems}
		clickCoordinates={clickCoordinates}
	/>);
}
